import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  DestroyRef,
  Directive,
  ElementRef,
  InputSignal,
  NgZone,
  WritableSignal,
  computed,
  effect,
  inject,
  input,
  PLATFORM_ID,
  signal,
  untracked
} from '@angular/core';
import { SwiperContainer } from 'swiper/element';
import { SwiperOptions } from 'swiper/types';
import Swiper from 'swiper';

/**
 * Drops params that switch a module off while that module never mounted: Swipers `destroyModule`
 * then calls `remove()` on a null `el` / `prevEl` and throws.
 * @param config Config about to be pushed into the running Swiper
 * @param swiper The running Swiper instance
 * @returns Config without those params
 */
function withoutUnmountedModuleTeardown(config: SwiperOptions, swiper: Swiper): SwiperOptions {
  // Swiper normalizes a `false` module param to `{ enabled: false }`, so both forms can arrive here
  const isOff = (option: boolean | { enabled?: boolean } | undefined): boolean =>
    option === false || (typeof option === 'object' && option !== null && 'enabled' in option && !option.enabled);
  const result: SwiperOptions = { ...config };
  if (isOff(result.pagination) && !swiper.pagination?.el) delete result.pagination;
  if (isOff(result.scrollbar) && !swiper.scrollbar?.el) delete result.scrollbar;
  if (isOff(result.navigation) && !swiper.navigation?.prevEl && !swiper.navigation?.nextEl) delete result.navigation;
  return result;
}

/**
 * Below this threshold Swiper's `loopFix` still moves slides, and a locked slider shows them rotated.
 * @param swiper The running Swiper instance
 * @returns Whether there are enough slides for loop mode
 */
function hasEnoughSlidesForLoop(swiper: Swiper): boolean {
  const slidesPerView = Math.ceil(Number(swiper.params.slidesPerView ?? 1));
  return swiper.slides.length >= slidesPerView + (swiper.params.slidesPerGroup ?? 1);
}

/** Window widths the pre-upgrade layout in styles.css switches at, the breakpoints the sliders use. */
const PRE_UPGRADE_WIDTHS = [0, 768, 992];

/**
 * Resolves a numeric Swiper param for a window width the way Swiper's breakpoints do (the largest
 * breakpoint not above the width wins). Ratio breakpoints (`@0.75`) are skipped.
 * @param config The Swiper config
 * @param width The window width in px
 * @param key The param to resolve
 * @returns The number, or undefined when it is not numeric (e.g. `slidesPerView: 'auto'`)
 */
function numericParamAt(config: SwiperOptions, width: number, key: 'slidesPerView' | 'spaceBetween'): number | undefined {
  let value = config[key];
  const breakpoints = config.breakpoints ?? {};
  Object.keys(breakpoints)
    .map(Number)
    .filter((breakpoint) => !Number.isNaN(breakpoint) && breakpoint <= width)
    .sort((a, b) => a - b)
    .forEach((breakpoint) => (value = breakpoints[breakpoint][key] ?? value));
  return typeof value === 'number' ? value : undefined;
}

/**
 * CSS variables for the server-rendered slider before Swiper is registered: slides per view and gap
 * for each width in `PRE_UPGRADE_WIDTHS`, so styles.css lays the slides out as wide as Swiper will.
 * @param config The Swiper config
 * @returns Style object with the custom properties, null without a config
 */
function preUpgradeStyle(config: SwiperOptions | undefined): Record<string, string> | null {
  if (!config) return null;
  const style: Record<string, string> = {};
  for (const width of PRE_UPGRADE_WIDTHS) {
    const slidesPerView = numericParamAt(config, width, 'slidesPerView');
    const spaceBetween = numericParamAt(config, width, 'spaceBetween');
    if (slidesPerView) style[`--swiper-pre-spv-${width}`] = String(slidesPerView);
    if (spaceBetween) style[`--swiper-pre-gap-${width}`] = `${spaceBetween}px`;
  }
  return style;
}

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[appSwiper]',
  host: {
    // Static, so it is on the element before it connects: a `[init]="false"` binding only lands in the
    // update pass, after an already defined `swiper-container` auto-initialized with default params.
    // Live updates skip the class-based params (`autoHeight` misses `swiper-autoheight` → height 0).
    init: 'false',
    '[style]': '_preUpgradeStyle()',
    '[attr.data-swiper-locked]': '_isLocked() ? "" : null',
    '[class.with-scrollbar]': '_hasScrollbar()',
    '[class.with-pagination]': '_hasPagination()'
  }
})
/**
 * Thin wrapper to configure a `SwiperContainer` via Angular input binding and
 * initialize it after view init. Supports autoplay start when configured.
 * Automatically hides navigation when not enough slides are present to scroll.
 */
export class SwiperDirective implements AfterViewInit {
  // Inputs
  /** Swiper configuration object applied to the underlying `SwiperContainer`. */
  config: InputSignal<SwiperOptions | undefined> = input<SwiperOptions | undefined>(undefined);

  // Properties
  private readonly el: ElementRef<SwiperContainer> = inject(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  /** Runs the Swiper timers outside the zone */
  private readonly ngZone = inject(NgZone);
  /** Distinguishes the server render from the browser. */
  private readonly platformId = inject(PLATFORM_ID);
  /** Whether the swiper is locked (not enough slides to scroll) */
  protected readonly _isLocked: WritableSignal<boolean> = signal(false);
  /** Whether the swiper has a scrollbar configured (cached across transient config resets) */
  protected readonly _hasScrollbar: WritableSignal<boolean> = signal(false);
  /** Whether the swiper has pagination configured (cached across transient config resets) */
  protected readonly _hasPagination: WritableSignal<boolean> = signal(false);
  /** Whether the config asks for loop mode (cached across transient config resets) */
  private readonly _requestedLoop: WritableSignal<boolean> = signal(false);
  /** Slide width variables for the layout before Swiper is registered (styles.css, `:not(:defined)`) */
  protected readonly _preUpgradeStyle = computed(() => preUpgradeStyle(this.config()));

  constructor() {
    // Update host class signals only on truthy config so transient `undefined`
    // assignments (used elsewhere to force re-init) don't toggle classes and
    // trigger a layout shift while padding/margin collapses and restores.
    effect(() => {
      const value = this.config();
      if (value) {
        this._hasScrollbar.set(!!value.scrollbar);
        this._hasPagination.set(!!value.pagination);
        this._requestedLoop.set(!!value.loop);
      }
    });

    // Push later config changes into a running Swiper. Assigning a parameter on the element runs its
    // own live-update (a changed direction goes through Swipers changeDirection), so a config that
    // depends on the device size stays in sync instead of freezing on its first value.
    effect(() => {
      const value = this.config();
      const el = this.el.nativeElement;
      const swiper = el.swiper as (Swiper & { initialized?: boolean }) | undefined;
      // Before initialization the element has no swiper yet and ngAfterViewInit does the assignment;
      // assigning to a not yet upgraded custom element would shadow its property setters.
      if (!value || !swiper?.initialized) return;
      // Swiper writes the slide size of its main axis inline, so drop it when the axis changes
      if (value.direction && swiper.params.direction !== value.direction) {
        el.querySelectorAll<HTMLElement>('swiper-slide').forEach((slide) => {
          slide.style.width = '';
          slide.style.height = '';
        });
      }
      const { loop, ...params } = withoutUnmountedModuleTeardown(value, swiper);
      Object.assign(el, params);
      this.syncLoop(swiper, !!loop);
    });
  }

  /**
   * Lifecycle: Assign configuration to element and initialize Swiper.
   * Waits for the `swiper-container` custom element to be defined (its runtime is
   * registered from a lazily-loaded chunk), then starts autoplay if configured and
   * sets up lock state detection.
   */
  async ngAfterViewInit(): Promise<void> {
    // The server has no custom elements, so `swiper-container` stays a plain element without `initialize()`.
    if (!isPlatformBrowser(this.platformId)) return;
    // The custom element may not be upgraded yet when the runtime is registered from a lazy chunk.
    if (typeof customElements !== 'undefined' && !customElements.get('swiper-container')) {
      await customElements.whenDefined('swiper-container');
    }
    const el = this.el.nativeElement;
    const cfg = this.config();
    // Assign the config unconditionally — even when the element already has a Swiper instance.
    // If the element auto-initialized before its config was bound, its parameters (breakpoints /
    // slidesPerView) default and every slide becomes full width; assigning the params here
    // live-updates the running instance via Swiper's property setters. `initialize()` is an
    // internal no-op once Swiper is already initialized, so this is safe to call either way.
    // Loop starts off: with too few slides Swiper would already rotate them on init
    const params: SwiperOptions = { ...cfg, loop: false };
    // An auto-initialized Swiper live-updates on assignment, so the same teardown guard as in the config effect applies
    Object.assign(el, el.swiper ? withoutUnmountedModuleTeardown(params, el.swiper) : params);
    // Outside the zone: the autoplay timer never ends, so the app would never become stable and the
    // hydration never complete (NG0506). Template listeners on Swiper events still run in the zone.
    const swiper = this.ngZone.runOutsideAngular(() => {
      el.initialize();
      const instance = el.swiper as Swiper;
      this.syncLoop(instance, !!cfg?.loop);
      if (cfg?.autoplay) {
        instance.autoplay.start();
      }
      return instance;
    });
    this.initLockState(swiper);
  }

  /**
   * Sets up the lock state detection on the swiper instance and keeps loop mode in sync.
   * When the swiper is locked (not enough slides to scroll), the `_isLocked` signal
   * is updated which reactively sets the `data-swiper-locked` attribute via host binding.
   * @param swiper The Swiper instance to observe
   */
  private initLockState(swiper: Swiper): void {
    if (!swiper) return;

    // Swiper emits these events synchronously from DOM/custom-element callbacks, which can run while a
    // computed/template is evaluating — a plain write would throw NG0600 there.
    const updateLock = (): void => untracked(() => this._isLocked.set(swiper.isLocked));
    // A breakpoint changes slidesPerView and with it the slides loop mode needs
    const syncLoop = (): void => this.syncLoop(swiper);

    updateLock();
    swiper.on('resize', updateLock);
    swiper.on('update', updateLock);
    swiper.on('lock', updateLock);
    swiper.on('unlock', updateLock);
    swiper.on('breakpoint', syncLoop);

    // Slides are rendered by Angular (@for), so filtering/paging replaces them without Swiper
    // noticing. Recheck the overflow — and with it lock state and loop mode — whenever the slides change.
    const slideObserver =
      typeof MutationObserver === 'undefined'
        ? undefined
        : new MutationObserver(() => {
            if (swiper.destroyed || (swiper as Swiper & { __preventObserver__?: boolean }).__preventObserver__) return;
            if (!this.el.nativeElement.isConnected) return;
            swiper.update();
            this.syncLoop(swiper);
          });
    slideObserver?.observe(this.el.nativeElement, { childList: true });

    this.destroyRef.onDestroy(() => {
      slideObserver?.disconnect();
      swiper.off('resize', updateLock);
      swiper.off('update', updateLock);
      swiper.off('lock', updateLock);
      swiper.off('unlock', updateLock);
      swiper.off('breakpoint', syncLoop);
    });
  }

  /**
   * Runs loop mode only while there are enough slides for it.
   * @param swiper The running Swiper instance
   * @param requestedLoop Whether the config asks for loop mode
   */
  private syncLoop(swiper: Swiper, requestedLoop: boolean = this._requestedLoop()): void {
    if (!swiper || swiper.destroyed) return;

    const shouldLoop = requestedLoop && hasEnoughSlidesForLoop(swiper);
    if (!!swiper.params.loop === shouldLoop) return;
    // Swiper's own toggle clears params.loop first, so its loopDestroy would skip restoring the order
    if (!shouldLoop) swiper.loopDestroy();
    Object.assign(this.el.nativeElement, { loop: shouldLoop });
  }
}
